/**
 * 🔧 ADMIN PAGE
 * Contest management and app settings
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gift, Trophy, Users, Clock, Sparkles, Trash2, Truck, DollarSign, ToggleLeft, ToggleRight, Vibrate, Radio, Smartphone } from 'lucide-react';
import { useContestStore, useSettingsStore, usePeoplePixelsStore, Contest } from '../store';
import styles from './Admin.module.css';

const PRIZE_EMOJIS = ['🍦', '🎁', '🎟️', '🏆', '⭐', '🎉'];

export function AdminPage() {
  const navigate = useNavigate();
  const contests = useContestStore((state) => state.contests);
  const createContest = useContestStore((state) => state.createContest);
  const pickWinner = useContestStore((state) => state.pickWinner);
  const endContest = useContestStore((state) => state.endContest);
  const clearContests = useContestStore((state) => state.clearContests);

  // Delivery settings
  const deliveryEnabled = useSettingsStore((state) => state.deliveryEnabled);
  const deliveryFee = useSettingsStore((state) => state.deliveryFee);
  const setDeliveryEnabled = useSettingsStore((state) => state.setDeliveryEnabled);
  const setDeliveryFee = useSettingsStore((state) => state.setDeliveryFee);

  // People Pixels
  const pixelsEnabled = usePeoplePixelsStore((state) => state.isEnabled);
  const setPixelsEnabled = usePeoplePixelsStore((state) => state.setEnabled);
  const triggerBuzz = usePeoplePixelsStore((state) => state.triggerBuzz);
  const sendBuzzToServer = usePeoplePixelsStore((state) => state.sendBuzzToServer);
  const sendColorToServer = usePeoplePixelsStore((state) => state.sendColorToServer);
  const sendClearToServer = usePeoplePixelsStore((state) => state.sendClearToServer);
  const fetchServerStats = usePeoplePixelsStore((state) => state.fetchServerStats);
  const serverStats = usePeoplePixelsStore((state) => state.serverStats);
  const connectedFans = usePeoplePixelsStore((state) => state.connectedFans);
  const lastError = usePeoplePixelsStore((state) => state.lastError);

  const [buzzSent, setBuzzSent] = useState(false);
  const [revealSent, setRevealSent] = useState(false);
  const [colorSending, setColorSending] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#FFD200');

  // Fetch server stats on mount and periodically
  useEffect(() => {
    if (pixelsEnabled) {
      fetchServerStats();
      const interval = setInterval(fetchServerStats, 5000); // Every 5 seconds
      return () => clearInterval(interval);
    }
  }, [pixelsEnabled, fetchServerStats]);

  const handleTestBuzz = async () => {
    setBuzzSent(true);
    await sendBuzzToServer();
    setTimeout(() => setBuzzSent(false), 2000);
  };

  const handleSendColor = async () => {
    setColorSending(true);
    await sendColorToServer(selectedColor, 15000); // 15 second duration
    setTimeout(() => setColorSending(false), 1000);
  };

  const handleClearAll = async () => {
    await sendClearToServer();
  };

  const handleReveal = () => {
    // Broadcast reveal to big screens
    window.dispatchEvent(new CustomEvent('pixel-reveal'));
    // Also via BroadcastChannel for other tabs
    const channel = new BroadcastChannel('people-pixels');
    channel.postMessage({ type: 'REVEAL' });
    setRevealSent(true);
  };

  const [prize, setPrize] = useState('Free Ice Cream');
  const [prizeEmoji, setPrizeEmoji] = useState('🍦');
  const [duration, setDuration] = useState(5);
  const [showCreate, setShowCreate] = useState(false);

  // Auto-check for expired contests
  useEffect(() => {
    const interval = setInterval(() => {
      contests.forEach((contest) => {
        if (contest.isActive && new Date(contest.endTime) <= new Date()) {
          // Contest expired - auto pick winner
          pickWinner(contest.id);
        }
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [contests, pickWinner]);

  const handleCreateContest = () => {
    if (!prize.trim()) return;
    createContest(prize, prizeEmoji, duration);
    setPrize('Free Ice Cream');
    setDuration(5);
    setShowCreate(false);
  };

  const getTimeRemaining = (endTime: string) => {
    const diff = new Date(endTime).getTime() - Date.now();
    if (diff <= 0) return 'Ended';

    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);

    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const activeContest = contests.find((c) => c.isActive && new Date(c.endTime) > new Date());

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Admin Panel</h1>
        <div style={{ width: 24 }} />
      </header>

      {/* Delivery Settings */}
      <div className={styles.settingsSection}>
        <h2 className={styles.settingsSectionTitle}>
          <Truck size={20} />
          In-Seat Delivery
        </h2>

        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <span className={styles.settingLabel}>Enable Delivery</span>
            <span className={styles.settingDesc}>Allow customers to get orders delivered to their seat</span>
          </div>
          <button
            className={styles.toggleBtn}
            onClick={() => setDeliveryEnabled(!deliveryEnabled)}
          >
            {deliveryEnabled ? (
              <ToggleRight size={32} className={styles.toggleOn} />
            ) : (
              <ToggleLeft size={32} className={styles.toggleOff} />
            )}
          </button>
        </div>

        {deliveryEnabled && (
          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <span className={styles.settingLabel}>Delivery Fee</span>
              <span className={styles.settingDesc}>Extra charge for in-seat delivery</span>
            </div>
            <div className={styles.feeInput}>
              <DollarSign size={16} />
              <input
                type="number"
                min="0"
                step="0.50"
                value={deliveryFee}
                onChange={(e) => setDeliveryFee(parseFloat(e.target.value) || 0)}
                className={styles.feeInputField}
              />
            </div>
          </div>
        )}
      </div>

      {/* People Pixels Section */}
      <div className={styles.settingsSection}>
        <h2 className={styles.settingsSectionTitle}>
          <Sparkles size={20} />
          People Pixels
        </h2>

        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <span className={styles.settingLabel}>Enable People Pixels</span>
            <span className={styles.settingDesc}>Transform the crowd into a living display</span>
          </div>
          <button
            className={styles.toggleBtn}
            onClick={() => setPixelsEnabled(!pixelsEnabled)}
          >
            {pixelsEnabled ? (
              <ToggleRight size={32} className={styles.toggleOn} />
            ) : (
              <ToggleLeft size={32} className={styles.toggleOff} />
            )}
          </button>
        </div>

        {pixelsEnabled && (
          <>
            {/* Server Connection Status */}
            {lastError && (
              <div className={styles.errorBanner}>
                <span>Server: {lastError}</span>
              </div>
            )}

            <div className={styles.pixelStats}>
              <div className={styles.pixelStat}>
                <Smartphone size={20} />
                <div>
                  <span className={styles.pixelStatValue}>{connectedFans.toLocaleString()}</span>
                  <span className={styles.pixelStatLabel}>Phones Connected</span>
                </div>
              </div>
              <div className={styles.pixelStat}>
                <Radio size={20} />
                <div>
                  <span className={styles.pixelStatValue}>
                    {serverStats ? Object.keys(serverStats.sections).length : 0}
                  </span>
                  <span className={styles.pixelStatLabel}>Sections Active</span>
                </div>
              </div>
            </div>

            {/* Color Picker */}
            <div className={styles.colorPickerRow}>
              <span className={styles.colorLabel}>Phone Color:</span>
              <div className={styles.colorOptions}>
                {['#FFD200', '#FF6B35', '#00AAFF', '#4CAF50', '#FFFFFF'].map((color) => (
                  <button
                    key={color}
                    className={`${styles.colorBtn} ${selectedColor === color ? styles.colorBtnActive : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setSelectedColor(color)}
                  />
                ))}
              </div>
            </div>

            <button
              className={`${styles.colorSendBtn} ${colorSending ? styles.colorSendBtnSent : ''}`}
              onClick={handleSendColor}
              disabled={colorSending}
              style={{ backgroundColor: selectedColor, color: selectedColor === '#FFFFFF' ? '#1a1a2e' : '#fff' }}
            >
              <Smartphone size={24} />
              <span>{colorSending ? 'SENT!' : 'SET ALL PHONES TO COLOR'}</span>
            </button>

            <button
              className={`${styles.buzzBtn} ${buzzSent ? styles.buzzBtnSent : ''}`}
              onClick={handleTestBuzz}
              disabled={buzzSent}
            >
              <Vibrate size={24} />
              <span>{buzzSent ? '📳📳📳 BUZZ SENT!' : 'BUZZ ALL PHONES (3x Vibrate)'}</span>
            </button>

            <button
              className={styles.pixelClearBtn}
              onClick={handleClearAll}
            >
              <span>CLEAR ALL PHONES</span>
            </button>

            <button
              className={`${styles.revealBtn} ${revealSent ? styles.revealBtnSent : ''}`}
              onClick={handleReveal}
              disabled={revealSent}
            >
              <Sparkles size={24} />
              <span>{revealSent ? '🎉 REVEALED!' : 'REVEAL TONIGHT\'S ART'}</span>
            </button>

            <p className={styles.buzzHint}>
              COLOR: Set all phone screens to selected color<br/>
              BUZZ: Send 3 vibrations to all phones<br/>
              REVEAL: Show the pixel art on big screens
            </p>
          </>
        )}
      </div>

      {/* Create Contest Button */}
      {!activeContest && !showCreate && (
        <button className={styles.createBtn} onClick={() => setShowCreate(true)}>
          <Gift size={24} />
          <span>Create New Contest</span>
        </button>
      )}

      {/* Create Contest Form */}
      {showCreate && (
        <div className={styles.createForm}>
          <h2 className={styles.formTitle}>
            <Sparkles size={20} />
            New Contest
          </h2>

          <div className={styles.formGroup}>
            <label className={styles.label}>Prize</label>
            <input
              type="text"
              className={styles.input}
              value={prize}
              onChange={(e) => setPrize(e.target.value)}
              placeholder="e.g. Free Ice Cream"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Prize Emoji</label>
            <div className={styles.emojiGrid}>
              {PRIZE_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  className={`${styles.emojiBtn} ${prizeEmoji === emoji ? styles.emojiBtnActive : ''}`}
                  onClick={() => setPrizeEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Duration (minutes)</label>
            <div className={styles.durationPicker}>
              {[1, 2, 5, 10, 15, 30].map((mins) => (
                <button
                  key={mins}
                  className={`${styles.durationBtn} ${duration === mins ? styles.durationBtnActive : ''}`}
                  onClick={() => setDuration(mins)}
                >
                  {mins}m
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button className={styles.submitBtn} onClick={handleCreateContest}>
              <Gift size={18} />
              Start Contest
            </button>
          </div>
        </div>
      )}

      {/* Active Contest */}
      {activeContest && (
        <div className={styles.activeContest}>
          <div className={styles.contestHeader}>
            <span className={styles.liveTag}>
              <span className={styles.liveDot} />
              LIVE
            </span>
            <span className={styles.contestEmoji}>{activeContest.prizeEmoji}</span>
          </div>

          <h2 className={styles.contestPrize}>{activeContest.prize}</h2>

          <div className={styles.contestStats}>
            <div className={styles.stat}>
              <Users size={18} />
              <span>{activeContest.entries.length} entries</span>
            </div>
            <div className={styles.stat}>
              <Clock size={18} />
              <span>{getTimeRemaining(activeContest.endTime)}</span>
            </div>
          </div>

          <button
            className={styles.pickWinnerBtn}
            onClick={() => pickWinner(activeContest.id)}
          >
            <Trophy size={20} />
            Pick Winner Now
          </button>
        </div>
      )}

      {/* Past Contests */}
      {contests.filter((c) => !c.isActive || new Date(c.endTime) <= new Date()).length > 0 && (
        <div className={styles.pastSection}>
          <h3 className={styles.sectionTitle}>Past Contests</h3>
          {contests
            .filter((c) => !c.isActive || new Date(c.endTime) <= new Date())
            .map((contest) => (
              <div key={contest.id} className={styles.pastContest}>
                <div className={styles.pastInfo}>
                  <span className={styles.pastEmoji}>{contest.prizeEmoji}</span>
                  <div>
                    <span className={styles.pastPrize}>{contest.prize}</span>
                    <span className={styles.pastMeta}>
                      {contest.entries.length} entries
                    </span>
                  </div>
                </div>
                {contest.winnerId ? (
                  <div className={styles.winnerBadge}>
                    <Trophy size={14} />
                    <span>{contest.winnerName}</span>
                  </div>
                ) : (
                  <span className={styles.noWinner}>No entries</span>
                )}
              </div>
            ))}

          <button className={styles.clearBtn} onClick={clearContests}>
            <Trash2 size={16} />
            Clear All
          </button>
        </div>
      )}

      {/* Empty State */}
      {contests.length === 0 && !showCreate && (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>🎉</span>
          <p className={styles.emptyText}>No contests yet</p>
          <p className={styles.emptySubtext}>Create a contest to engage your customers!</p>
        </div>
      )}
    </div>
  );
}
